import React, { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Panel,
  Title,
  FormElement,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  SpacingVertical,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Flex,
} from "mailup-web-components";
import useAsyncSelect from "Hooks/useAsyncSelect/useAsyncSelect";

import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import { INPUT_MESSAGES_MAX_ITEMS } from "Constants";
import {
  fetchGroupById,
  fetchGroups,
} from "Pages/ImportWizard/recoil/asyncSelectGroups";
import { importFileWizardState } from "Pages/ImportWizard/recoil/state/state";
import { addGroupCallback } from "Pages/ImportWizard/recoil/callbacks";
import iconPlus from "../../../../icons/plus-solid.svg";
import ModalNewGroup from "./ModalNewGroup";
import { modalNewGroupState } from "Pages/ImportWizard/recoil/state/stateStep4";
import { gainsightTrack } from "mailup-react-helpers";

const Groups: FC = () => {
  const intl = useIntl();

  const [forceCollapse, setForceCollapse] = useState(false);

  const { GroupIDs } = useRecoilValue(importFileWizardState);
  const setImportFileWizardState = useSetRecoilState(importFileWizardState);

  const addGroup = useRecoilCallback(addGroupCallback, []);

  const { options, loading, handleSearch, hint, hintNoResults, updateOptions } =
    useAsyncSelect({
      key: "import-file-wizard-groups",
      value: GroupIDs, // recipients ===  RECIPIENTS_TEST_GROUP ? [idTestDefault] : includeGroups,
      fetchItems: () => fetchGroups(""),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: "Load.MailupCommon.InputStatusLoading",
        }),
        noResults: intl.formatMessage({
          id: "Load.MailupCommon.InputStatusNoMatches",
        }),
        moreResults: intl.formatMessage({
          id: "Load.MailupCommon.InputStatusTooManyItems",
        }),
      },
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
    });
  const handleChange = (value: number[]) => {
    gainsightTrack("mup_importWizard_clickSelectToChooseAGroup", {});

    setImportFileWizardState((prev) => ({
      ...prev,
      GroupIDs: value,
    }));
  };

  const handleSelectGroupSubmit = async (s: string): Promise<void> => {
    const newGroupId = await addGroup(s);

    if (newGroupId !== undefined && newGroupId > 0) {
      void updateOptions(newGroupId);
    }

    setForceCollapse(true);

    setTimeout(() => {
      setForceCollapse(false);
    }, 250);
  };

  const handleSelectGroupExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch("");
    }
  };

  return (
    <>
      <ModalNewGroup />
      <Panel>
        <Title variant="M">
          {intl.formatMessage({ id: "Load.FileImport.SelectGroupsTitle" })}
        </Title>
        <SpacingVertical variant="16" />
        <FormElement
          label={intl.formatMessage({
            id: "Load.FileImport.SelectGroupsLabel",
          })}
          hint={intl.formatMessage({
            id: "Load.FileImport.SelectGroupsHinit",
          })}
          LabelRightComponent={LabelRightComponent}
        >
          <Select
            multiple
            searchable
            variant={SELECT_VARIANT_WITH_CHECKBOX}
            value={GroupIDs}
            hint={hint}
            hintNoResults={hintNoResults}
            onChange={handleChange}
            onSubmit={handleSelectGroupSubmit}
            loading={loading}
            options={options}
            onSearch={handleSearch}
            onExpandCollapse={handleSelectGroupExpandCollapse}
            placeholder={intl.formatMessage({
              id: "Load.FileImport.SelectGroupsPlaceHolder",
            })}
          />
        </FormElement>
      </Panel>
    </>
  );
};
export default Groups;

const LabelRightComponent: FC = () => {
  const intl = useIntl();

  const setModalNewGourp = useSetRecoilState(modalNewGroupState);

  const handleAddGroup = () => {
    gainsightTrack("mup_importWizard_clickAddGroup", {});

    setModalNewGourp((prev) => ({
      ...prev,
      Show: true,
    }));
  };

  return (
    <Link variant={LINK_VARIANT_HIGHLIGHT} onClick={handleAddGroup}>
      <Flex alignItems="center" gap="4px">
        <img
          src={iconPlus}
          alt={intl.formatMessage({ id: "Load.FileImport.AddGroup" })}
        />
        {intl.formatMessage({
          id: "Load.FileImport.AddGroup",
        })}
      </Flex>
    </Link>
  );
};
